// Window-frame slot recognizer for the EditorContext projection.
// PRD: GRA-8 (MSSQL projection pivot), design §2.2. G11: pure text scan,
// NO web-tree-sitter — this is the legal projection-side body the future
// tree-sitter swap replaces behind the identical signature.
//
// GRA8-ROLL-1 extracted this dialect-neutral window-frame family out of
// editorContextSlot.ts. The frame-extent keyword vocabulary
// (`ROWS|RANGE BETWEEN <extent> [AND <extent>]`) is standard SQL, so the
// detector is engine-agnostic and stays reusable by later cross-engine slices.
//
// The slot lives inside an `OVER(... ORDER BY ...)` body, so the text clause
// machine names the enclosing clause `order-by` (a confidently-named clause) —
// `resolveSlotKind`'s gate therefore never fires for it from `deriveContext`'s
// `unknown`-only path. The resolver calls `detectMssqlWindowFrameSlot` directly
// (a clause-independent text scan, mirroring the datepart detector) and emits
// the directional vocabulary. `resolveSlotKind` still recognises the coarse
// `'window-frame'` slot via `windowFrameSlot` for the ambiguous-arm /
// future-tree-swap path and the provenance seam.

import { tokenizeUntil, type ClauseToken } from './editorContextClause';
import { upper } from './editorContextSlotShared';

export type MssqlWindowFrameSlot = 'frame-start' | 'frame-end';

/**
 * Detect a window-frame extent slot from the source text, independent of the
 * clause machine. Returns `'frame-start'` for the cursor right after
 * `ROWS|RANGE BETWEEN`, `'frame-end'` for the cursor after the `AND` that
 * separates the two frame extents, or `null` otherwise. Pure text scan —
 * `engine` selects the dialect lexing (bracket / backtick / dollar-quote).
 */
export function detectMssqlWindowFrameSlot(
  source: string,
  stmtStart: number,
  offset: number,
  engine: string,
): MssqlWindowFrameSlot | null {
  return windowFrameSlot(tokenizeUntil(source, stmtStart, offset, engine));
}

const FRAME_MODE_KEYWORDS: ReadonlySet<string> = new Set(['ROWS', 'RANGE']);

export function windowFrameSlot(tokens: readonly ClauseToken[]): MssqlWindowFrameSlot | null {
  const prev = tokens[tokens.length - 1];
  if (!prev) return null;
  const prevUpper = upper(prev);

  // frame-start: cursor sits right after `ROWS|RANGE BETWEEN`.
  if (prevUpper === 'BETWEEN') {
    const before = upper(tokens[tokens.length - 2]);
    if (before && FRAME_MODE_KEYWORDS.has(before)) return 'frame-start';
    return null;
  }

  // frame-end: cursor sits right after the `AND` separating the two extents of
  // a `ROWS|RANGE BETWEEN <extent> AND <extent>` frame. The first extent is at
  // most two tokens (`UNBOUNDED PRECEDING`, `N PRECEDING`, `CURRENT ROW`), so
  // we look back a short window for a `BETWEEN` preceded by `ROWS`/`RANGE`.
  if (prevUpper === 'AND') {
    for (let i = tokens.length - 2; i >= Math.max(0, tokens.length - 6); i -= 1) {
      const u = upper(tokens[i]);
      if (u === 'BETWEEN') {
        const before = upper(tokens[i - 1]);
        if (before && FRAME_MODE_KEYWORDS.has(before)) return 'frame-end';
        return null;
      }
      // A second `AND` before reaching `BETWEEN` means we are past the frame's
      // extent separator (or in an unrelated boolean chain) — not a frame slot.
      if (u === 'AND') return null;
    }
  }

  return null;
}
